import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Send, Loader2, CheckCircle, Building2, Users, Briefcase, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { APP_NAME } from "../config/branding";
import apiClient from "../utils/apiClient";
import Footer from "../components/Footer";
import Seo from "../components/Seo";

const TEAM_SIZES = [
  { value: "1-5", label: "1–5" },
  { value: "6-10", label: "6–10" },
  { value: "11-25", label: "11–25" },
  { value: "26-50", label: "26–50" },
  { value: "50+", label: "50+" },
];

const USAGE_OPTIONS = [
  { value: "light", label: "Light" },
  { value: "moderate", label: "Moderate" },
  { value: "heavy", label: "Heavy" },
  { value: "not_sure", label: "Not sure yet" },
];

const USE_CASES = [
  { value: "grievance_analysis", label: "Grievance & arbitration analysis" },
  { value: "contract_review", label: "Collective agreement review" },
  { value: "negotiation_prep", label: "Negotiation preparation" },
  { value: "team_training", label: "Team training & onboarding" },
  { value: "compliance", label: "Compliance & audit" },
  { value: "other", label: "Other" },
];

const TIMELINES = [
  { value: "immediate", label: "Immediate — ready to start" },
  { value: "1-3_months", label: "1–3 months" },
  { value: "3-6_months", label: "3–6 months" },
  { value: "exploring", label: "Just exploring options" },
];

const ContactSales = () => {
  const [searchParams] = useSearchParams();
  const planKey = searchParams.get("plan_key") || "";

  const [form, setForm] = useState({
    name: "", email: "", organization: "", job_title: "",
    team_size: "", expected_usage: "", primary_use_case: "",
    use_case_detail: "", timeline: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (planKey) fetchPlan();
  }, [planKey]);

  const fetchPlan = async () => {
    try {
      const res = await apiClient.get("/pricing", { skipErrorToast: true });
      const found = (res.data.plans || []).find((p) => p.plan_key === planKey);
      if (found) setPlan(found);
    } catch (err) {
      console.debug('Could not fetch plan details:', err);
    }
  };

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const useCaseText = form.primary_use_case === "other"
    ? form.use_case_detail
    : USE_CASES.find((u) => u.value === form.primary_use_case)?.label || form.primary_use_case;

  const isValid = form.name && form.email && form.organization &&
    form.job_title && form.team_size && form.expected_usage &&
    form.primary_use_case && form.timeline &&
    (form.primary_use_case !== "other" || form.use_case_detail);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      await apiClient.post("/contact-sales", {
        name: form.name,
        email: form.email,
        organization: form.organization,
        job_title: form.job_title,
        team_size: form.team_size,
        expected_usage: form.expected_usage,
        primary_use_case: useCaseText,
        timeline: form.timeline,
        notes: form.notes || null,
        plan_key: planKey || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <PageShell>
        <div className="max-w-lg mx-auto text-center pt-16 pb-20 px-4">
          <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-foreground mb-3" data-testid="contact-sales-success">
            Request Received
          </h1>
          <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto">
            Thank you for your interest in the Organization plan. Our team will review your
            requirements and respond within one business day.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/pricing"><Button variant="outline">Back to Pricing</Button></Link>
            <Link to="/"><Button>Return Home</Button></Link>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-8 pb-20">
        <Button
          variant="ghost" size="sm"
          onClick={() => window.history.back()}
          className="gap-1.5 text-muted-foreground hover:text-foreground mb-6"
          data-testid="contact-sales-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>

        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-2" data-testid="contact-sales-heading">
            {plan ? `${plan.display_name} Plan` : "Organization Plan"} — Custom Pricing
          </h1>
          <p className="text-base text-muted-foreground leading-relaxed max-w-lg">
            Pricing scales with team size, usage volume, and deployment requirements.
            Complete the form below and we'll prepare a tailored proposal.
          </p>
        </div>

        {/* Plan features preview */}
        {plan && plan.feature_list && (
          <Card className="mb-8 border-border/60">
            <CardContent className="pt-5 pb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Included in this plan</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {plan.feature_list.map((f, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-muted-foreground">{f}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-sales-form">
          {/* Contact info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="text-sm">Full Name *</Label>
              <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)}
                placeholder="Jane Smith" required data-testid="input-name" />
            </div>
            <div>
              <Label htmlFor="email" className="text-sm">Work Email *</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                placeholder="jane@organization.com" required data-testid="input-email" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="organization" className="text-sm">
                <Building2 className="w-3.5 h-3.5 inline mr-1 mb-0.5" />Organization Name *
              </Label>
              <Input id="organization" value={form.organization} onChange={(e) => set("organization", e.target.value)}
                placeholder="CUPE Local 123" required data-testid="input-organization" />
            </div>
            <div>
              <Label htmlFor="job_title" className="text-sm">
                <Briefcase className="w-3.5 h-3.5 inline mr-1 mb-0.5" />Job Title *
              </Label>
              <Input id="job_title" value={form.job_title} onChange={(e) => set("job_title", e.target.value)}
                placeholder="Chief Steward" required data-testid="input-job-title" />
            </div>
          </div>

          {/* Team & usage */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">
                <Users className="w-3.5 h-3.5 inline mr-1 mb-0.5" />Team Size *
              </Label>
              <Select value={form.team_size} onValueChange={(v) => set("team_size", v)}>
                <SelectTrigger data-testid="select-team-size"><SelectValue placeholder="Select team size" /></SelectTrigger>
                <SelectContent>
                  {TEAM_SIZES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Expected Monthly Usage *</Label>
              <Select value={form.expected_usage} onValueChange={(v) => set("expected_usage", v)}>
                <SelectTrigger data-testid="select-expected-usage"><SelectValue placeholder="Select usage level" /></SelectTrigger>
                <SelectContent>
                  {USAGE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Use case */}
          <div>
            <Label className="text-sm">Primary Use Case *</Label>
            <Select value={form.primary_use_case} onValueChange={(v) => set("primary_use_case", v)}>
              <SelectTrigger data-testid="select-use-case"><SelectValue placeholder="Select primary use case" /></SelectTrigger>
              <SelectContent>
                {USE_CASES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.primary_use_case === "other" && (
              <Input
                className="mt-2"
                value={form.use_case_detail}
                onChange={(e) => set("use_case_detail", e.target.value)}
                placeholder="Please describe your use case"
                data-testid="input-use-case-detail"
              />
            )}
          </div>

          {/* Timeline */}
          <div>
            <Label className="text-sm">
              <Clock className="w-3.5 h-3.5 inline mr-1 mb-0.5" />Timeline *
            </Label>
            <Select value={form.timeline} onValueChange={(v) => set("timeline", v)}>
              <SelectTrigger data-testid="select-timeline"><SelectValue placeholder="When do you need this?" /></SelectTrigger>
              <SelectContent>
                {TIMELINES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes" className="text-sm">Additional Notes</Label>
            <Textarea
              id="notes" value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Any specific requirements, questions, or context..."
              rows={3} data-testid="input-notes"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600" data-testid="contact-sales-error">{error}</p>
          )}

          <Button
            type="submit" disabled={!isValid || submitting}
            className="w-full h-11 text-base gap-2"
            data-testid="contact-sales-submit-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
            ) : (
              <><Send className="w-4 h-4" /> Request Proposal</>
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            We typically respond within one business day. Looking for an individual plan?{" "}
            <Link to="/pricing" className="text-foreground hover:underline">View pricing</Link>
          </p>
        </form>
      </div>
    </PageShell>
  );
};

const PageShell = ({ children }) => (
  <div className="min-h-screen bg-background flex flex-col">
    <Seo
      title="Contact Sales | CBAIQ"
      description="Talk to the CBAIQ team about Organization plans — multi-user collective agreement intelligence for labour relations teams."
      path="/contact-sales"
    />
    <header className="border-b border-border/40 sticky top-0 z-40 bg-background/95 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          <Link to="/" className="font-heading font-semibold text-lg tracking-tight">{APP_NAME}</Link>
          <div className="flex items-center gap-3">
            <Link to="/pricing"><Button variant="ghost" size="sm">Pricing</Button></Link>
            <Link to="/register"><Button size="sm" data-testid="get-started-btn">Get Started</Button></Link>
          </div>
        </div>
      </div>
    </header>
    <div className="flex-1">{children}</div>
    <footer className="border-t border-border py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6"><Footer /></div>
    </footer>
  </div>
);

export default ContactSales;
